import { AlertTriangle, ArrowRight, CheckCircle2, LockKeyhole } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Where a submitted parcel stands with dispatch.
 *
 * There is nothing to press here. Confirming the label attached decides
 * readiness, a ready parcel goes to dispatch on its own, and every later event
 * that changes a readiness input (a replaced label, the store moving) decides it
 * again. This card only reports the result: waiting for the label, ready with
 * what happens next, or the reasons the parcel cannot go.
 *
 * Those reasons are the server's final assertion. Each one is meant to have
 * been stopped earlier - at checkout, at payment, at packing - so none of them
 * is a packing instruction, and the card says so rather than suggesting that
 * checking again would change anything.
 */

function formatDecisionTime(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString('en-PH', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function ParcelReadinessCard({ order }) {
  const location = useLocation();
  if (Number(order?.workflowVersion) !== 2) return null;

  const readiness = order?.readiness || { status: 'NOT_CHECKED', blockers: [] };
  const status = readiness.status || 'NOT_CHECKED';
  const label = order?.parcelLabel;
  const blockers = Array.isArray(readiness.blockers) ? readiness.blockers : [];
  const decisionTime = formatDecisionTime(readiness.decidedAt);
  const handoffPath = `/business/orders/fulfillment/${order.orderId}/handoff${location.search}`;

  return (
    <section aria-labelledby="parcel-readiness-title" className="rounded-2xl border border-neutral-200 p-4 dark:border-neutral-800">
      <h3 id="parcel-readiness-title" className="font-semibold">Dispatch</h3>

      {!label ? (
        <div className="mt-3 flex gap-3 rounded-xl border border-dashed border-neutral-300 p-4 text-sm text-neutral-600 dark:border-neutral-700 dark:text-neutral-300">
          <LockKeyhole className="h-5 w-5 shrink-0" aria-hidden="true" />
          <p>Generate the label, print it and attach it, then confirm it is attached. The parcel is then checked and sent to dispatch automatically.</p>
        </div>
      ) : !label.attachedAt ? (
        // Whatever the stored verdict says, a label that is not confirmed on the
        // parcel is waiting on the packing table, not on the system.
        <div role="status" className="mt-3 flex gap-3 rounded-xl border border-info bg-info-subtle p-4 text-sm text-info">
          <LockKeyhole className="h-5 w-5 shrink-0" aria-hidden="true" />
          <div><p className="font-semibold">Waiting for the label to be attached</p><p className="mt-1">Print the label, attach it to the parcel, then confirm it is attached. Dispatch starts only after that.</p></div>
        </div>
      ) : status === 'READY' ? (
        <div role="status" className="mt-3 rounded-xl border border-success bg-success-subtle p-4 text-success">
          <div className="flex gap-2">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <div>
              <p className="font-semibold">Submitted for dispatch</p>
              <p className="mt-1 text-sm text-success">A Rider will be assigned automatically. The label is confirmed on the parcel; keep it ready for pickup.</p>
              {decisionTime ? <p className="mt-2 text-xs text-success">Submitted {decisionTime}</p> : null}
              <Link to={handoffPath} className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl border border-success px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">
                Go to rider handoff<ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div role="alert" className="mt-3 rounded-xl border border-warning bg-warning-subtle p-4 text-warning">
          <div className="flex gap-2">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <p className="font-semibold">This parcel can&apos;t be dispatched</p>
              {blockers.length ? (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                  {blockers.map((blocker, index) => <li key={`${blocker.code || 'blocker'}-${index}`}>{blocker.message || 'The order no longer meets the conditions for dispatch.'}</li>)}
                </ul>
              ) : <p className="mt-1 text-sm">The order no longer meets the conditions for dispatch.</p>}
              <p className="mt-3 text-xs leading-5 text-warning">Packing can&apos;t fix this. Each reason above says what can still be done.</p>
              {decisionTime ? <p className="mt-1 text-xs text-warning">Checked {decisionTime}</p> : null}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
